import type { FileReviewFile, FileReviewSheet } from "@/lib/api/databook";

const CATEGORIES = [
  { id: "trial-balances", label: "Trial balances", types: ["trial_balance"] },
  { id: "management-accounts", label: "Management accounts", types: ["pnl", "management_accounts"] },
  { id: "balance-sheets", label: "Balance sheets", types: ["balance_sheet", "provisions"] },
  { id: "sales", label: "Sales and debtors", types: ["revenue", "ar_aging", "ar_by_debtor", "accrued_income", "sundry_debtors"] },
  { id: "purchases", label: "Purchases and creditors", types: ["purchases_by_supplier", "ap_aging", "ap_by_creditor", "prepayments", "sundry_creditors", "accrued_expenses"] },
  { id: "payroll", label: "Payroll", types: ["fte"] },
  { id: "bank", label: "Bank and treasury", types: ["bank_statements", "cash_flow", "fx_rates"] },
  { id: "inventory", label: "Inventory", types: ["inventory_aging", "inventory_by_type", "wip_by_type"] },
  { id: "fixed-assets", label: "Fixed assets and capex", types: ["fixed_assets_register", "intangibles", "capex", "capex_commitments"] },
  { id: "adjustments", label: "Adjustments and reconciliations", types: ["normalisation", "eliminations", "reconciliation"] },
  { id: "kpis", label: "KPIs", types: ["kpi"] },
  { id: "other", label: "Other source data", types: [] },
  { id: "mixed", label: "Mixed source data", types: [] },
];

export interface FileReviewSheetRow {
  documentId: string;
  filename: string;
  sheet: FileReviewSheet;
}

export interface FileReviewCategory {
  id: string;
  label: string;
  rows: FileReviewSheetRow[];
  used: number;
}

export function groupFileReview(files: FileReviewFile[]): FileReviewCategory[] {
  const groups = CATEGORIES.map((category): FileReviewCategory => ({
    id: category.id, label: category.label, rows: [], used: 0,
  }));
  const groupsById = new Map(groups.map((group) => [group.id, group]));
  const categoryByType = new Map(CATEGORIES.flatMap((category) =>
    category.types.map((type) => [type, category.id] as const)
  ));

  for (const file of files) {
    if (file.source_type !== "excel") continue;
    for (const sheet of file.sheets) {
      // Classify by analyzed semantic types, never filenames or sheet names.
      // A mixed sheet stays intact so its status and counts remain sheet-level.
      const categories = new Set(sheet.tables.map((table) => categoryByType.get(table.table_type) ?? "other"));
      const category = categories.size > 1 ? "mixed" : [...categories][0] ?? "other";
      const group = groupsById.get(category)!;
      group.rows.push({ documentId: file.document_id, filename: file.filename, sheet });
      if (sheet.status === "used") group.used++;
    }
  }
  return groups.filter((group) => group.rows.length > 0);
}
